import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, mergeMap, switchMap, tap} from "rxjs/operators";
import {Observable, of, map} from 'rxjs';
import {tableFromIPC, tableToIPC} from 'apache-arrow';
import {
  AppsGroupsData,
  DomainResolution,
  DownloadReportData,
  ReportsData,
  HeatmapFlowDetail, UserInfo, TelemetryInfo, TelemetryPushInfo, FlowDescriptionRule, TemporalParameter
} from "@app/netscope/model/data-models";
import {environment} from "../../environments/environment";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {ColumnTable} from "arquero";
import * as aq from "arquero";
import { parquetReadObjects } from 'hyparquet';
import {NetscopeUtils} from "@app/netscope/netscope-utils";

// Classes defined as models for DC Netscope components

// Answer of the API: a Success carrying the element, or a Failure. Response is a union, so that checking
// `response instanceof Success` gives a typed `element`, and the `ok` fields keep a Success<A> from being taken for a
// Response<B> (an empty Failure would otherwise match any object).
export class Success<C> {
    readonly ok = true;
    constructor(public element: C) {
    }
}

export class Failure<C> {
    readonly ok = false;
}

export type Response<C> = Success<C> | Failure<C>;

export type ReportType = "network" | "apps_groups";

export class AppsGroupsFiles {
  files: string[];
  generatedFiles: string[];
}

// Resources the members rules of an apps group match: 'total' counts all of them, 'members' holds the first ones
// by name, as a pattern such as '*' matches the whole inventory
export class AppsGroupsRulesPreview {
  total: number;
  members: { uuid: string, name: string, type: string }[];
}

export class FlowDescriptionsTransport {
  flowsDescriptions: FlowDescriptionRule[];
}

export class LastReleaseInfo {
    version: string;
    product: string;
    date: number;
    message: string;
}



type NetscopeResponseType = 'json' | 'arraybuffer' | 'blob' | 'text';


// Below is defined the DC Netscope service

@Injectable({
    providedIn: 'root'
})
export class NetscopeApiService {

    sessionUuid: string;

    constructor(private http: HttpClient, private netscopeUiService: NetscopeUiService) {
      this.sessionUuid = NetscopeUtils.generateUuid();
    }

    generateHeaders() {
        // const username = sessionStorage.getItem('username');
        // const password = sessionStorage.getItem('password');
        // const header = {
        //   'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        // };
        // return header;
        return {
          'isProduction': environment.production ? "production" : "development",
          'sessionUuid': this.sessionUuid
          // 'isProduction': environment.production ? "production" : "production"
        };
    }

    getNetscopeSettings() {
      let response = [
        {"option":"agent_url", "value": environment.apiUrl},
        {"option":"token_api", "value":""}
      ];
      return of(response);
    }

    doGetHttpRequestNew < TGeneric > (urlDcNetscopeApi: string, params, encodeBase64: boolean, isAbsoluteUrl: boolean = false, ignoreErrors: boolean=false): Observable <Response<TGeneric> > {
        return this.doGetHttpRequest(urlDcNetscopeApi, params, Success<TGeneric>, Failure, isAbsoluteUrl, encodeBase64, ignoreErrors);
    }

    doPostHttpRequestNew < TGeneric > (urlDcNetscopeApi: string, params, encodeBase64: boolean, isAbsoluteUrl: boolean = false, ignoreErrors: boolean=false): Observable <Response<TGeneric> > {
        return this.doPostHttpRequest(urlDcNetscopeApi, params, Success<TGeneric>, Failure, isAbsoluteUrl, encodeBase64, ignoreErrors);
    }

    doGetHttpRequest < TGeneric, TSuccess extends TGeneric, TFailure extends TGeneric > (urlDcNetscopeApi: string, params, constructorSuccess: new(something) => TSuccess, constructorFailure: new() => TFailure, isAbsoluteUrl: boolean=false, encodeBase64: boolean=false, ignoreErrors: boolean=false): Observable <TGeneric > {
        let postParamsHeaders = {
            headers: this.generateHeaders()
        };

        var postParams = Object.assign(postParamsHeaders, params);

        let data = postParams;
        if (encodeBase64) {
          let encodedData = btoa(JSON.stringify(params));
          data = encodedData;
        }

        let observable = this.getNetscopeSettings().pipe(mergeMap(settings => {
            let url = urlDcNetscopeApi;
            if (! isAbsoluteUrl) {
                let agentUrl = "http://localhost:5000";
                settings.filter((setting) => setting.option === "agent_url").map((setting) => {
                    agentUrl = setting.value;
                })
                url = `${environment.apiUrl}${urlDcNetscopeApi}`;
            }
            return this.http.get < any[] > (url, data);
        }));
        return observable.pipe(
            switchMap(result => {
              this.netscopeUiService.setLoadingErrorStage(false);
              return of(new constructorSuccess(result));
            }),
            catchError(error => {
              if (! ignoreErrors) {
                this.netscopeUiService.setLoadingErrorStage(true);
              }
              return of(new constructorFailure())
            })
        );
    }

    doPostHttpRequest < TGeneric, TSuccess extends TGeneric, TFailure extends TGeneric > (urlDcNetscopeApi: string, params, constructorSuccess: new(something) => TSuccess, constructorFailure: new() => TFailure, isAbsoluteUrl: boolean=false, encodeBase64: boolean=false, ignoreErrors: boolean=false, responseType: NetscopeResponseType = 'json'): Observable <TGeneric > {
        const headers = new HttpHeaders(this.generateHeaders());

        var postParams = Object.assign({}, params);

        let data = postParams;
        if (encodeBase64) {
          let encodedData = btoa(JSON.stringify(params));
          data = encodedData;
        }

        let observable = this.getNetscopeSettings().pipe(mergeMap(settings => {
            let url = urlDcNetscopeApi;
            if (! isAbsoluteUrl) {
                let agentUrl = "http://localhost:5000";
                settings.filter((setting) => setting.option === "agent_url").map((setting) => {
                    agentUrl = setting.value;
                })
                url = `${environment.apiUrl}${urlDcNetscopeApi}`;
            }
            return this.http.post < any[] > (url, data, {headers, responseType: responseType as 'json'});
        }));
        return observable.pipe(
            switchMap(result => {
              this.netscopeUiService.setLoadingErrorStage(false);
              return of(new constructorSuccess(result));
            }),
            catchError(error => {
              console.error(error);
              if (! ignoreErrors) {
                this.netscopeUiService.setLoadingErrorStage(true);
              }
              return of(new constructorFailure())
            })
        );
    }


    public getLastInfrastructureVersion(): Observable<Response<any>> {
        return this.doGetHttpRequest(
            "/infrastructure/version/last",
            {},
            Success<any>,
            Failure<any>
        );
    }

    public getDailyTimestamps(): Observable<Response<any>> {
        return this.doGetHttpRequest("/networking/visualisation/timeranges/daily", {},
            Success<any>, Failure<any>);
    }

    // public getDashboardDataForFilter(vmsUuids, hostsUuids, only_flows_with_src_and_dst_in_the_filter, maxTimestamp) {
    //     let params = {
    //         vmsUuids: vmsUuids,
    //         hostsUuids: hostsUuids,
    //         only_flows_with_src_and_dst_in_the_filter: only_flows_with_src_and_dst_in_the_filter,
    //         day: maxTimestamp
    //     };
    //     let url = `/dashboard/data_for_filter`;
    //     return this.doPostHttpRequest(url, params, Success<any>, Failure<any>);
    // }

    public getDashboardGraphDataForFilter(vmsUuids, hostsUuids, only_flows_with_src_and_dst_in_the_filter): Observable<Response<any>> {
        let params = {
            vmsUuids: vmsUuids,
            hostsUuids: hostsUuids,
            only_flows_with_src_and_dst_in_the_filter: only_flows_with_src_and_dst_in_the_filter
        };
        let url = `/v2/history`;
        return this.doPostHttpRequest(url, params, Success<any>, Failure<any>);
    }

    public getDomainResolution(ip): Observable<Response<DomainResolution>> {
        let params = {
            srcIp: ip
        };
        let url = `/resolve_domain`;
        return this.doPostHttpRequest(url, params, Success<DomainResolution>,
            Failure<DomainResolution>);
    }

    public getHeatmapFlowsData(srcUuid, dstUuid, directionMode: string): Observable<Response<any>> {
        let params = {
            srcUuid: srcUuid,
            dstUuid: dstUuid,
            direction_mode: directionMode
        };
        let url = `/heatmap/get_flow_data`;
        return this.doPostHttpRequest(url, params, Success<any>, Failure<any>);
    }

    public getHeatmapFlowsDetails(srcUuid, dstUuid, timestamp, timeResolution = "hourly", directionMode: string): Observable<Response<HeatmapFlowDetail>> {
        let params = {
            srcUuid: srcUuid,
            dstUuid: dstUuid,
            timestamp: timestamp,
            timeResolution: timeResolution,
            direction_mode: directionMode
        };
        let url = `/heatmap/get_flow_details`;
        return this.doPostHttpRequest(url, params, Success<HeatmapFlowDetail>, Failure<HeatmapFlowDetail>);
    }

    public getCustomProtocolsRules(): Observable<Response<any>> {
        return this.doPostHttpRequest("/custom_protocols/rules/fetch", {}, Success<any>, Failure<any>);
    }

    public pushCustomProtocols(custom_rules): Observable<Response<object>> {
        let params = {
            custom_rules: custom_rules
        };
        let url = `/custom_protocols/rules/push`;
        return this.doPostHttpRequest(url, params, Success<object>, Failure<object>);
    }

    public getFlowsDescriptions(): Observable<Response<FlowDescriptionsTransport>> {
        let url = "/flows_descriptions/fetch"
        let params = {}
        return this.doPostHttpRequestNew<FlowDescriptionsTransport>(url, params, false, false, true);
    }

    public pushFlowsDescriptions(flowsDescriptions: FlowDescriptionsTransport) {
        let params = {
            flowsDescriptions: flowsDescriptions,
        }
        let url = `/flows_descriptions/push`;
        return this.doPostHttpRequestNew<object>(url, params, false, false, false);
    }

    public getExternalIps(): Observable<Response<any>> {
        return this.doPostHttpRequest("/external_ips/fetch", {}, Success<any>, Failure<any>);
    }

    public pushExternalIps(externalIps): Observable<Response<object>> {
        let params = {
            external_ips: externalIps,
        }
        let url = `/external_ips/push`;
        return this.doPostHttpRequest(url, params, Success<object>, Failure<object>);
    }

    public getAppsGroups(appsGroupsMode: string): Observable<Response<AppsGroupsData>> {
        let params = {
            apps_groups_mode: appsGroupsMode,
            // Read by no route anymore, and kept only so that an older API still understands the request
            use_automatic_apps_groups: appsGroupsMode == "tags"
        };
        let url = `/apps_groups/groups/fetch`;
        return this.doPostHttpRequest(url, params, Success<AppsGroupsData>, Failure<AppsGroupsData>);
    }

    public pushAppsGroups(apps_groups_mode: string, apps_groups): Observable<Response<object>> {
        let params = {
            apps_groups_mode: apps_groups_mode,
            apps_groups: apps_groups,
        };
        let url = `/apps_groups/groups/push`;
        return this.doPostHttpRequest(url, params, Success<object>, Failure<object>);
    }

    public previewAppsGroupsRules(include: string[], exclude: string[]) {
        let params = {
            include: include,
            exclude: exclude,
        };
        let url = `/apps_groups/rules/preview`;
        return this.doPostHttpRequestNew<AppsGroupsRulesPreview>(url, params, false, false, true);
    }

    public getAppsGroupsFiles() {
        let url = `/apps_groups/files/fetch`;
        return this.doPostHttpRequestNew<AppsGroupsFiles>(url, {}, false, false, false);
    }

    public createAppsGroupsFile(name: string) {
        let params = {
            name: name,
        };
        let url = `/apps_groups/files/create`;
        return this.doPostHttpRequestNew<object>(url, params, false, false, false);
    }

    public deleteAppsGroupsFile(name: string) {
        let params = {
            name: name,
        };
        let url = `/apps_groups/files/delete`;
        return this.doPostHttpRequestNew<object>(url, params, false, false, false);
    }

    public getUserInfo() {
        let params = {};
        let url = `${environment.oauth2Url}/userinfo`;
        return this.doGetHttpRequestNew<UserInfo>(url, params, true, true, true);
    }

    public getTelemetryInfo() {
        let params = {};
        let url = `/telemetry_info`;
        return this.doGetHttpRequestNew<TelemetryInfo>(url, params, false, false, true);
    }

    public sendTelemetry(info: TelemetryPushInfo) {
        let telemetryUrl = "https://www.easyvirt.com/data.php";
        return this.doPostHttpRequestNew<Object>(telemetryUrl, info, false, true, true);
    }

    public getServicesStatus() {
        let params = {};
        let url = `/api/health/services/all`;
        return this.doGetHttpRequestNew<Object>(url, params, false);
    }

    public getVcentersStatus(): Observable<Response<any>> {
        let params = {};
        let url = ``;
        return this.doPostHttpRequest(url, params, Success<any>, Failure<any>);
    }

    public getOverallStatus(): Observable<Response<any>> {
        let params = {};
        let url = `/api/overall/status`;
        return this.doPostHttpRequest(url, params, Success<any>, Failure<any>);
    }

    public getReportsData(): Observable<Response<ReportsData>> {
        let params = {};
        let url = `/api/reports/data`;
        return this.doPostHttpRequest(url, params, Success<ReportsData>, Failure<ReportsData>);
    }

    // The network activity report unless another type is given, the apps groups one covering the files given
    public requestReportGeneration(start, end, sendByEmail: boolean, reportType?: ReportType,
                                   appsGroupsFiles?: string[]): Observable<Response<object>> {
        let params = {
            start: start,
            end: end,
            sendByEmail: sendByEmail,
            reportType: reportType,
            appsGroupsFiles: appsGroupsFiles,
        };
        let url = `/api/reports/generate`;
        return this.doPostHttpRequest(url, params, Success<object>, Failure<object>);
    }

    public requestReportDownload(reportUuid: string): Observable<Response<DownloadReportData>> {
        let params = {
            reportUuid: reportUuid
        };
        let url = `/api/reports/download`;
        return this.doPostHttpRequest(url, params, Success<DownloadReportData>, Failure<DownloadReportData>);
    }

    public requestReportDeletion(reportUuid: string): Observable<Response<DownloadReportData>> {
        let params = {
            report_uuid: reportUuid
        };
        let url = `/api/reports/delete`;
        return this.doPostHttpRequest(url, params, Success<DownloadReportData>, Failure<DownloadReportData>);
    }

    public heatmapGetSourceResources(): Observable<Response<Array<string>>> {
        let params = {};
        let url = `/heatmap/get_src_resources`;
        return this.doPostHttpRequest(url, params, (Success<Array<string>>), (Failure<Array<string>>));
    }

    public heatmapLinkedResources(src_resource_uuid): Observable<Response<Array<string>>> {
        let params = {
            src_resource_uuid: src_resource_uuid
        };
        let url = `/heatmap/get_linked_resources`;
        return this.doPostHttpRequest(url, params, (Success<Array<string>>), (Failure<Array<string>>));
    }

    public callHeartBeat(): Observable<Response<Object>> {
        let params = {
        };
        let url = `/heartbeat`;
        return this.doGetHttpRequest(url, params, (Success<Object>), (Failure<Object>));

    }

    public getPublishedUpdateInfos(): Observable<Response<LastReleaseInfo>> {
        let telemetryUrl = "https://mirror1.easyvirt.com/v8/dcnetscope_release.json";
        return this.doGetHttpRequestNew<LastReleaseInfo>(telemetryUrl, {}, false, true, true);
    }

    public executeQueryRemotely(query: string, queryUuid: string): Observable<Response<any[]>> {
        let params = {
            query: query,
            queryUuid: queryUuid
        };
        let url = `/data/query`;
        return this.doPostHttpRequest(url, params, (Success<Array<string>>), (Failure<Array<string>>));
    }

    public executeQueryRemotelyArrow(query: string, queryUuid: string): Observable<Response<any[]>> {
        let params = {
            query: query,
            queryUuid: queryUuid,
            mode: "arrow"
        };
        let url = `/data/query`;
        return this.doPostHttpRequest(url, params, (Success<ArrayBuffer>), (Failure<ArrayBuffer>), false, false, false, "arraybuffer").pipe(
            map((buf: Response<ArrayBuffer>): Response<any[]> => {
              if (!(buf instanceof Success)) {
                return new Failure<any[]>();
              }
              let table = tableFromIPC(new Uint8Array(buf.element));
              return new Success(table.toArray().map(row => row.toJSON()));
            })
        );
    }

    public executeQueryRemotelyParquet(query: string, queryUuid: string): Observable<Response<any[]>> {
        let params = {
            query: query,
            queryUuid: queryUuid,
            mode: "parquet"
        };
        let url = `/data/query`;
        return this.doPostHttpRequest(url, params, (Success<ArrayBuffer>), (Failure<ArrayBuffer>), false, false, false, "arraybuffer").pipe(
            // parquetReadObjects is asynchronous: its rows are awaited before being wrapped in a Success
            switchMap(async (buf: Response<ArrayBuffer>): Promise<Response<any[]>> => {
              if (!(buf instanceof Success)) {
                return new Failure<any[]>();
              }
              return new Success(await parquetReadObjects({file: buf.element}));
            })
        );
    }

    /**
     * Ask the backend to anonymise the data of this browser session, and download the password protected
     * archive it builds. The archive can then be sent to the Easyvirt support, which uploads it in the
     * administration page of a DC Netscope to replay what the user sees.
     */
    downloadAnonymisedUiData = (params: TemporalParameter, password: string): Observable<Response<Blob>> => {
      let payload = {
        timePeriod: params.timeSelection,
        startTime: params.startTime,
        endTime: params.endTime,
        password: password
      };

      let observable = this.doPostHttpRequest("/ui_data/anonymised/download", payload,
        Success<Blob>, Failure<Blob>,
        false, false, true, 'blob');

      return observable.pipe(tap((response: Response<Blob>) => {
        if (! (response instanceof Success)) {
          return;
        }

        let exportedFilename = `anonymised_ui_data_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.dcns`;

        let link = document.createElement("a");
        if (link.download === undefined) { // feature detection
          return;
        }

        // Browsers that support HTML5 download attribute
        let url = URL.createObjectURL(response.element);
        link.setAttribute("href", url);
        link.setAttribute("download", exportedFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }));
    }
}
